import axios from "../../axios";
import logger from "../Logger";
import { buildApiV3Url } from "../Utils";
import { deduplicateRequest } from "./apiDeduplicationService";

export const PREF_KEY_PENDING_CERTIFICATE_EMAIL = "pending-certificate-email";
export const PREF_KEY_OPT_IN_ESCALATED_NOTIFICATIONS = "opt-in-escalated-notifications";
export const PREF_KEY_OPT_IN_VALIDATION_NOTIFICATIONS = "opt-in-validation-notifications";

// Type definitions
interface Preference {
    preference: string;
    preferenceValue: string;
}

interface PreferencesResponse {
    data?: {
        value?: Preference[];
    };
}

// Module-level promise to track in-flight request (for deduplication only, no caching)
let preferencesPromise: Promise<Preference[]> | null = null;

/**
 * Fetches all ECM preferences in a single API call.
 * Uses deduplication to prevent concurrent duplicate calls.
 * @returns {Promise<Preference[]>} Array of all preferences
 */
export const fetchAllPreferences = async (): Promise<Preference[]> => {
    preferencesPromise = deduplicateRequest(preferencesPromise, async () => {
        try {
            const preferenceResponse = await axios.get<PreferencesResponse>(
                buildApiV3Url("ecm-preferences"),
                {
                    withCredentials: true,
                }
            );

            return (preferenceResponse?.data as { value?: Preference[] })?.value || [];
        } catch (error: unknown) {
            // Fire and forget - logger is designed to not block
            void logger(2, "error", {
                message: "Error fetching ECM preferences:",
                jsStack: error instanceof Error ? error.stack : String(error),
            });
            return [];
        }
    });

    try {
        return await preferencesPromise;
    } finally {
        preferencesPromise = null;
    }
};

/**
 * Gets a specific preference by name from the cached preferences
 * @param {string} preferenceName - The name of the preference to retrieve
 * @returns {Promise<Preference|null>} The preference object or null if not found
 */
export const getPreference = async (preferenceName: string): Promise<Preference | null> => {
    const preferences = await fetchAllPreferences();
    return preferences.find(pref => pref.preference === preferenceName) || null;
};

/**
 * Gets a parsed preference value by name
 * @param {string} preferenceName - The name of the preference to retrieve
 * @returns {Promise<unknown|null>} The parsed preference value or null if not found
 */
export const getPreferenceValue = async (preferenceName: string): Promise<unknown | null> => {
    const preference = await getPreference(preferenceName);
    if (!preference?.preferenceValue) {
        return null;
    }

    try {
        return JSON.parse(preference.preferenceValue);
    } catch (error: unknown) {
        // Fire and forget - logger is designed to not block
        void logger(2, "error", {
            message: `Error parsing preference value for ${preferenceName}:`,
            jsStack: error instanceof Error ? error.stack : String(error),
        });
        return null;
    }
};

/**
 * Clears the in-flight promise (useful for testing)
 */
export const clearPreferencesPromise = (): void => {
    preferencesPromise = null;
};

/**
 * Gets multiple preferences in a single call
 * @param {Array<string>} preferenceNames - Array of preference names to retrieve
 * @returns {Promise<Record<string, unknown>>} Object with preference names as keys and values as values
 */
export const getMultiplePreferences = async (
    preferenceNames: string[]
): Promise<Record<string, unknown>> => {
    const preferences = await fetchAllPreferences();
    const result: Record<string, unknown> = {};

    preferenceNames.forEach(name => {
        const preference = preferences.find(pref => pref.preference === name);
        if (preference?.preferenceValue) {
            try {
                result[name] = JSON.parse(preference.preferenceValue);
            } catch (error: unknown) {
                // Fire and forget - logger is designed to not block
                void logger(2, "error", {
                    message: `Error parsing preference value for ${name}:`,
                    jsStack: error instanceof Error ? error.stack : String(error),
                });
                result[name] = null;
            }
        } else {
            result[name] = null;
        }
    });

    return result;
};
